import React from 'react';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Register required Chart.js components
ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

// Custom plugin to draw second-level amenity labels under grouped bars
const amenityLabelsPlugin = {
  id: 'amenityLabels',
  afterDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    const yScale = scales.y;

    if (!xScale || !yScale) return;

    ctx.save();
    ctx.fillStyle = 'black';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = '12px sans-serif';

    // Array of amenities repeated per day (must be passed in options.plugins.amenityLabels.labels)
    const amenityLabels = chart.options.plugins?.amenityLabels?.labels;
    if (!amenityLabels) {
      ctx.restore();
      return;
    }

    const tickPositions = xScale.getTicks().map(t => xScale.getPixelForTick(t.index));
    const daysCount = xScale.ticks.length;
    const amenitiesPerDay = amenityLabels.length / daysCount;

    for (let dayIndex = 0; dayIndex < daysCount; dayIndex++) {
      const dayX = tickPositions[dayIndex];
      // Calculate group width based on tick spacing or fallback
      const nextTick = tickPositions[dayIndex + 1];
      const prevTick = tickPositions[dayIndex - 1];
      const groupWidth = nextTick
        ? nextTick - dayX
        : dayX - (prevTick || dayX - 50);

      for (let a = 0; a < amenitiesPerDay; a++) {
        const labelIndex = dayIndex * amenitiesPerDay + a;
        const label = amenityLabels[labelIndex];

        // X position is start of group + fraction inside the group
        const x = dayX - groupWidth / 2 + (a + 0.5) * (groupWidth / amenitiesPerDay);
        const y = chartArea.bottom + 20; // 20px below the x-axis

        ctx.fillText(label, x, y);
      }
    }

    ctx.restore();
  },
};

ChartJS.register(amenityLabelsPlugin);

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const amenities = ['clubhouse-pool--jacuzzi', 'clubhouse-gym', 'lounge']; // your amenities

// Dummy sample aggregated data by day and amenity by hour counts
// For simplicity, random numbers here. Replace with your real aggregated data.
const sampleAggregatedData = {
  'Sun': {
    'clubhouse-pool--jacuzzi': [2, 1, 0, 0, 3, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    'clubhouse-gym': [0, 0, 1, 2, 2, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    'lounge': [1, 0, 0, 0, 0, 1, 2, 3, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
  'Mon': {
    'clubhouse-pool--jacuzzi': [1, 0, 2, 1, 0, 0, 0, 3, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    'clubhouse-gym': [0, 1, 0, 1, 2, 2, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    'lounge': [0, 0, 1, 0, 1, 0, 0, 2, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
  // ... fill for all days Sun-Sat similarly
};

// Helper: sum array values (hour counts)
const sumArray = (arr) => arr.reduce((a, b) => a + b, 0);

// Prepare datasets for Chart.js
const datasets = [];

// For stacked hour segments, we'll combine hours 0-23 stacked per amenity per day
// But since the user wants amenities stacked inside each day's group, grouped by day,
// we create a dataset for each amenity+hour to stack inside amenity bars per day.

amenities.forEach((amenity, aIndex) => {
  for (let hour = 0; hour < 24; hour++) {
    datasets.push({
      label: `${amenity} hour ${hour}`,
      data: daysOfWeek.map(day => {
        const dayData = sampleAggregatedData[day];
        if (!dayData) return 0;
        const hourData = dayData[amenity];
        return hourData ? hourData[hour] || 0 : 0;
      }),
      backgroundColor: `hsl(${(aIndex * 100 + hour * 10) % 360}, 70%, 60%)`,
      stack: `${amenity}`,
    });
  }
});

const data = {
  labels: daysOfWeek,
  datasets,
};

const options = {
  responsive: true,
  scales: {
    x: {
      stacked: true,
      title: {
        display: true,
        text: 'Day of Week',
      },
    },
    y: {
      stacked: true,
      beginAtZero: true,
      title: {
        display: true,
        text: 'Visit Count',
      },
    },
  },
  plugins: {
    legend: {
      position: 'right',
      labels: {
        filter: (legendItem) => {
          // Only show legend for amenities, not hours
          return amenities.some(a => legendItem.text.startsWith(a));
        },
      },
    },
    title: {
      display: true,
      text: 'Visits by Amenity & Hour, Grouped by Day',
    },
    amenityLabels: {
      // Repeat amenities per day in order (daysOfWeek.length * amenities.length)
      labels: [].concat(...Array(daysOfWeek.length).fill(amenities.map(a => a.replace(/-/g, ' ')))),
    },
  },
};

export default function VisitsByAmenityAndHourChart() {
  return <Bar data={data} options={options} />;
}
